import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  Autocomplete,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Tooltip from "@mui/material/Tooltip";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getAllocationsBySOWId,
  createSOWAllocation,
  updateSOWAllocation,
  deleteSOWAllocation,
} from "../../http/requests/sow";
import { getLinkedProjectsBySOWId } from "../../http/requests/sow";
import { getProjectAdminDetails } from "../../http/requests/project-admin";
import { getEmployeeDropdownData } from "../../http/requests/session";
import { getSOWData } from "../../redux/actions";
import { toastOptions } from "../../common/utils/toasterOptions";

const projectId = (p) => p?.projectId ?? p?.ProjectId;
const projectName = (p) => p?.projectName ?? p?.ProjectName ?? "";
const clientName = (p) => p?.clientName ?? p?.ClientName ?? "";
const projectLabel = (p) => `${projectName(p)} (${clientName(p)})`;
const empId = (e) => e?.azureId ?? e?.AzureId ?? "";
const empLabel = (e) => e?.name ?? e?.Name ?? e?.email ?? e?.Email ?? "";

const formatDate = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.split("T")[0] || d;
  return d;
};

const SOWAllocationModal = ({ open, onClose, sow, onSuccess }) => {
  const dispatch = useDispatch();
  const sowListClientFilterId = useSelector((s) => s.MODULES.sowListClientFilterId);
  const [loading, setLoading] = useState(false);
  const [allocations, setAllocations] = useState([]);
  const [linkedProjects, setLinkedProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const sowId = sow?.sowId ?? sow?.SOWId;
  const sowName = sow?.sowName ?? sow?.SOWName ?? "";
  const clientId = sow?.clientId ?? sow?.ClientId;
  const sowStart = useMemo(() => {
    const d = sow?.sowStartDate ?? sow?.SOWStartDate;
    return d ? dayjs(formatDate(d)) : null;
  }, [sow]);
  const sowEnd = useMemo(() => {
    const d = sow?.sowEndDate ?? sow?.SOWEndDate;
    return d ? dayjs(formatDate(d)) : null;
  }, [sow]);

  useEffect(() => {
    if (!open) return;
    setRows([]);
    setEditingId(null);
    const load = async () => {
      setLoading(true);
      try {
        const [allocRes, linkedIdsRes, projectsRes, employeesRes] = await Promise.all([
          sowId ? getAllocationsBySOWId(sowId) : Promise.resolve([]),
          sowId ? getLinkedProjectsBySOWId(sowId) : Promise.resolve([]),
          getProjectAdminDetails(1, 500, "ASC", "projectName", "", false),
          getEmployeeDropdownData(),
        ]);
        const allocList = Array.isArray(allocRes) ? allocRes : allocRes?.data ?? allocRes?.Data ?? [];
        const linkedIds = Array.isArray(linkedIdsRes) ? linkedIdsRes : [];
        const projects = Array.isArray(projectsRes) ? projectsRes : projectsRes?.data ?? projectsRes?.Data ?? [];
        const emps = Array.isArray(employeesRes) ? employeesRes : employeesRes?.data ?? employeesRes?.Data ?? [];

        setAllProjects(projects);
        setLinkedProjects(projects.filter((p) => linkedIds.includes(projectId(p))));
        setEmployees(emps);
        setAllocations(allocList);
        setRows(
          allocList.map((a) => ({
            sowProjectAllocationId: a.sowProjectAllocationId ?? a.SOWProjectAllocationId,
            projectId: a.projectId ?? a.ProjectId,
            empId: a.empId ?? a.EmpId ?? "",
            allocationStartDate: a.allocationStartDate ?? a.AllocationStartDate,
            allocationEndDate: a.allocationEndDate ?? a.AllocationEndDate,
            allocationPercent: a.allocationPercent ?? a.AllocationPercent ?? null,
            isNew: false,
          }))
        );
      } catch (err) {
        console.error("Failed to load allocation data:", err);
        toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to load data", toastOptions);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, sowId]);

  const handleClose = () => {
    setRows([]);
    setEditingId(null);
    onClose();
  };

  const addRow = () => {
    setRows((prev) => {
      const newRow = {
        sowProjectAllocationId: null,
        projectId: null,
        empId: "",
        allocationStartDate: sowStart?.format("YYYY-MM-DD") ?? "",
        allocationEndDate: sowEnd?.format("YYYY-MM-DD") ?? "",
        allocationPercent: null,
        isNew: true,
      };
      const next = [...prev, newRow];
      setEditingId("new-" + (next.length - 1));
      return next;
    });
  };

  const updateRow = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeLocalRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    if (editingId === "new-" + index) setEditingId(null);
  };

  const handleCopyRow = (index) => {
    const row = rows[index];
    const copiedRow = {
      sowProjectAllocationId: null,
      projectId: row.projectId,
      empId: "",
      allocationStartDate: row.allocationStartDate ?? "",
      allocationEndDate: row.allocationEndDate ?? "",
      allocationPercent: row.allocationPercent ?? null,
      allocationHours: null,
      isNew: true,
    };
    setRows((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, copiedRow);
      setEditingId("new-" + (index + 1));
      return next;
    });
  };

  const validateRow = (row) => {
    if (!row.projectId) return "Select a project.";
    if (!row.empId) return "Select an employee.";
    const start = dayjs(formatDate(row.allocationStartDate));
    const end = dayjs(formatDate(row.allocationEndDate));
    if (!start.isValid()) return "Invalid start date.";
    if (!end.isValid()) return "Invalid end date.";
    if (sowStart && start.isBefore(sowStart, "day")) return "Start date must be within SOW date range.";
    if (sowEnd && start.isAfter(sowEnd, "day")) return "Start date must be within SOW date range.";
    if (sowStart && end.isBefore(sowStart, "day")) return "End date must be within SOW date range.";
    if (sowEnd && end.isAfter(sowEnd, "day")) return "End date must be within SOW date range.";
    if (end.isBefore(start, "day")) return "End date cannot be before start date.";
    const pct = row.allocationPercent != null && row.allocationPercent !== "" ? Number(row.allocationPercent) : null;
    if (pct != null && (isNaN(pct) || pct < 0 || pct > 100)) return "Allocation % must be 0–100.";
    return null;
  };

  const handleSaveNew = async (index) => {
    const row = rows[index];
    const err = validateRow(row);
    if (err) {
      toast.error(err, toastOptions);
      return;
    }
    setSavingId("new-" + index);
    try {
      await createSOWAllocation(sowId, {
        sowId,
        projectId: row.projectId,
        empId: row.empId,
        allocationStartDate: formatDate(row.allocationStartDate),
        allocationEndDate: formatDate(row.allocationEndDate),
        allocationPercent: row.allocationPercent != null && row.allocationPercent !== "" ? Number(row.allocationPercent) : null,
      });
      toast.success("Allocation added", toastOptions);
      const allocRes = await getAllocationsBySOWId(sowId);
      const allocList = Array.isArray(allocRes) ? allocRes : allocRes?.data ?? allocRes?.Data ?? [];
      setAllocations(allocList);
      setRows(
        allocList.map((a) => ({
          sowProjectAllocationId: a.sowProjectAllocationId ?? a.SOWProjectAllocationId,
          projectId: a.projectId ?? a.ProjectId,
          empId: a.empId ?? a.EmpId ?? "",
          allocationStartDate: a.allocationStartDate ?? a.AllocationStartDate,
          allocationEndDate: a.allocationEndDate ?? a.AllocationEndDate,
          allocationPercent: a.allocationPercent ?? a.AllocationPercent ?? null,
          isNew: false,
        }))
      );
      setEditingId(null);
      dispatch(getSOWData({ pageNo: 1, pageSize: 10, sortBy: "sowName", sortDir: "ASC", searchData: "", clientId: sowListClientFilterId }));
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || err?.data?.errorMessage || err?.message || "Failed to add allocation", toastOptions);
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveEdit = async (index) => {
    const row = rows[index];
    const id = row.sowProjectAllocationId;
    if (!id) return handleSaveNew(index);
    const err = validateRow(row);
    if (err) {
      toast.error(err, toastOptions);
      return;
    }
    setSavingId(id);
    try {
      await updateSOWAllocation(id, {
        sowId,
        projectId: row.projectId,
        empId: row.empId,
        allocationStartDate: formatDate(row.allocationStartDate),
        allocationEndDate: formatDate(row.allocationEndDate),
        allocationPercent: row.allocationPercent != null && row.allocationPercent !== "" ? Number(row.allocationPercent) : null,
      });
      toast.success("Allocation updated", toastOptions);
      setEditingId(null);
      const allocRes = await getAllocationsBySOWId(sowId);
      const allocList = Array.isArray(allocRes) ? allocRes : allocRes?.data ?? allocRes?.Data ?? [];
      setAllocations(allocList);
      setRows(
        allocList.map((a) => ({
          sowProjectAllocationId: a.sowProjectAllocationId ?? a.SOWProjectAllocationId,
          projectId: a.projectId ?? a.ProjectId,
          empId: a.empId ?? a.EmpId ?? "",
          allocationStartDate: a.allocationStartDate ?? a.AllocationStartDate,
          allocationEndDate: a.allocationEndDate ?? a.AllocationEndDate,
          allocationPercent: a.allocationPercent ?? a.AllocationPercent ?? null,
          isNew: false,
        }))
      );
      dispatch(getSOWData({ pageNo: 1, pageSize: 10, sortBy: "sowName", sortDir: "ASC", searchData: "", clientId: sowListClientFilterId }));
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || err?.data?.errorMessage || err?.message || "Failed to update allocation", toastOptions);
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (row) => {
    const id = row.sowProjectAllocationId;
    if (!id) {
      removeLocalRow(rows.indexOf(row));
      return;
    }
    setDeletingId(id);
    try {
      await deleteSOWAllocation(id);
      toast.success("Allocation removed", toastOptions);
      setRows((prev) => prev.filter((r) => r.sowProjectAllocationId !== id));
      setAllocations((prev) => prev.filter((a) => (a.sowProjectAllocationId ?? a.SOWProjectAllocationId) !== id));
      dispatch(getSOWData({ pageNo: 1, pageSize: 10, sortBy: "sowName", sortDir: "ASC", searchData: "", clientId: sowListClientFilterId }));
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || err?.data?.errorMessage || err?.message || "Failed to delete allocation", toastOptions);
    } finally {
      setDeletingId(null);
    }
  };

  const isEditing = (row, index) => editingId === (row.isNew ? "new-" + index : row.sowProjectAllocationId);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          minWidth: 1500,
          maxWidth: "min(720px, calc(100vw - 22rem))",
          // Center in main content area (right of sidebar): offset by half of sidebar width (20rem)
          position: "fixed",
          left: "calc(50% + 10rem)",
          top: "50%",
          transform: "translate(-50%, -50%)",
          margin: 0,
        },
      }}
    >
      <DialogTitle sx={{ color: "#1773bc", fontWeight: 600, pb: 0 }}>
        SOW Allocation
      </DialogTitle>
      <Divider sx={{ borderColor: "#1773bc", opacity: 0.5 }} />
      <DialogContent sx={{ pt: 1.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", fontSize: "0.9375rem" }}>
            <Box sx={{ minWidth: 90, color: "text.secondary" }}>SOW Name:</Box>
            <Box sx={{ fontWeight: 500 }}>{sowName || "—"}</Box>
            <Box sx={{ minWidth: 90, color: "text.secondary", ml: 2 }}>SOW Start:</Box>
            <Box>{sowStart ? sowStart.format("DD-MM-YYYY") : "—"}</Box>
            <Box sx={{ minWidth: 90, color: "text.secondary", ml: 2 }}>SOW End:</Box>
            <Box>{sowEnd ? sowEnd.format("DD-MM-YYYY") : "—"}</Box>
          </Box>

          {loading ? (
            <Box sx={{ py: 2, textAlign: "center", color: "text.secondary" }}>Loading...</Box>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 0.5 }}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ backgroundColor: "#1773bc" }}
                  onClick={addRow}
                  disabled={loading || !linkedProjects.length}
                >
                  Add Allocation
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, "& .MuiTableCell-root": { fontSize: "0.9375rem" } }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: "#1773bc0d", fontSize: "0.9375rem" }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: "#1773bc0d", fontSize: "0.9375rem" }}>Employee</TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: "#1773bc0d", fontSize: "0.9375rem" }}>Start Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: "#1773bc0d", fontSize: "0.9375rem" }}>End Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: "#1773bc0d", fontSize: "0.9375rem" }}>Allocation %</TableCell>
                      <TableCell sx={{ fontWeight: 700, backgroundColor: "#1773bc0d", width: 120, fontSize: "0.9375rem" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                <TableBody>
                  {rows.map((row, index) => {
                    const editing = isEditing(row, index);
                    const projectOption = linkedProjects.find((p) => projectId(p) === row.projectId);
                    const employeeOption = employees.find((e) => empId(e) === row.empId);
                    return (
                      <TableRow key={row.sowProjectAllocationId ?? "new-" + index}>
                        <TableCell>
                          {editing ? (
                            <Autocomplete
                              size="small"
                              options={linkedProjects}
                              value={projectOption ?? null}
                              onChange={(_, v) => updateRow(index, "projectId", projectId(v) ?? null)}
                              getOptionLabel={(p) => projectLabel(p)}
                              isOptionEqualToValue={(a, b) => projectId(a) === projectId(b)}
                              renderInput={(params) => <TextField {...params} placeholder="Project" />}
                              sx={{ minWidth: 200 }}
                            />
                          ) : (
                            projectOption ? projectLabel(projectOption) : "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {editing ? (
                            <Autocomplete
                              size="small"
                              options={employees}
                              value={employeeOption ?? null}
                              onChange={(_, v) => updateRow(index, "empId", empId(v) ?? "")}
                              getOptionLabel={(e) => empLabel(e)}
                              isOptionEqualToValue={(a, b) => empId(a) === empId(b)}
                              renderInput={(params) => <TextField {...params} placeholder="Employee" />}
                              sx={{ minWidth: 220 }}
                            />
                          ) : (
                            employeeOption ? empLabel(employeeOption) : "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {editing ? (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={row.allocationStartDate ? dayjs(formatDate(row.allocationStartDate)) : null}
                                onChange={(d) => updateRow(index, "allocationStartDate", d ? d.format("YYYY-MM-DD") : null)}
                                minDate={sowStart}
                                maxDate={sowEnd}
                                renderInput={(params) => <TextField {...params} size="small" />}
                              />
                            </LocalizationProvider>
                          ) : (
                            formatDate(row.allocationStartDate) || "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {editing ? (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={row.allocationEndDate ? dayjs(formatDate(row.allocationEndDate)) : null}
                                onChange={(d) => updateRow(index, "allocationEndDate", d ? d.format("YYYY-MM-DD") : null)}
                                minDate={sowStart}
                                maxDate={sowEnd}
                                renderInput={(params) => <TextField {...params} size="small" />}
                              />
                            </LocalizationProvider>
                          ) : (
                            formatDate(row.allocationEndDate) || "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {editing ? (
                            <TextField
                              type="number"
                              size="small"
                              value={row.allocationPercent ?? ""}
                              onChange={(e) => updateRow(index, "allocationPercent", e.target.value === "" ? null : e.target.value)}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                              sx={{ width: 90 }}
                            />
                          ) : (
                            row.allocationPercent != null ? Number(row.allocationPercent) : "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {editing ? (
                            <>
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => (row.isNew ? handleSaveNew(index) : handleSaveEdit(index))}
                                  disabled={savingId !== null}
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy row (except employee)">
                                <IconButton size="small" onClick={() => handleCopyRow(index)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => (row.isNew ? removeLocalRow(index) : handleDelete(row))} disabled={deletingId !== null}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Edit">
                                <IconButton size="small" color="primary" onClick={() => setEditingId(row.isNew ? "new-" + index : row.sowProjectAllocationId)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy row (except employee)">
                                <IconButton size="small" onClick={() => handleCopyRow(index)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDelete(row)} disabled={deletingId !== null}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SOWAllocationModal;
